import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from '../../../../i18n';

export interface PuzzlePlayerProps {
  imageUrl: string;
  onSubmit: (answer: string, points: number) => void;
  disabled?: boolean;
  /** Round end timestamp (Date.now() when intro + game time ends); used to compute points from time remaining */
  roundEndTimeMs: number;
  timeLimitMs: number;
  /** When true, show only empty bordered box and "Loading puzzle pieces..."; pieces appear when intro ends */
  isIntro?: boolean;
  /** Replaces default footer hint (e.g. when host is playing — no separate host TV). */
  footerHint?: string;
  /** During intro when host is playing: show countdown instead of "Loading puzzle pieces…" */
  introCountdownSeconds?: number | null;
}

const QUADRANT_POSITIONS: Array<{ x: string; y: string }> = [
  { x: '0%', y: '0%' },
  { x: '100%', y: '0%' },
  { x: '0%', y: '100%' },
  { x: '100%', y: '100%' },
];

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Puzzle player: empty 2×2 box (theme border) in the center; 4 pieces around it.
 * Drag or tap to place pieces into slots; solve when each slot has the correct quadrant.
 * Points = 100 * (timeRemaining / timeLimit).
 */
export const PuzzlePlayer: React.FC<PuzzlePlayerProps> = ({
  imageUrl,
  onSubmit,
  disabled = false,
  roundEndTimeMs,
  timeLimitMs,
  isIntro = false,
  footerHint,
  introCountdownSeconds = null,
}) => {
  const { t } = useTranslation();
  const [slots, setSlots] = useState<(number | null)[]>(() => [null, null, null, null]);
  const [pool, setPool] = useState<number[]>(() => shuffle([0, 1, 2, 3]));
  const [selected, setSelected] = useState<{ source: 'slot' | 'pool'; index: number } | null>(null);
  const [dragging, setDragging] = useState<{ source: 'slot' | 'pool'; index: number } | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const submittedRef = useRef(false);
  /** When all 4 slots are filled: 'success' = correct order (green blink), 'wrong' = incorrect (red blink), then cleared after 1s */
  const [borderFlash, setBorderFlash] = useState<'success' | 'wrong' | null>(null);
  const allFilledFlashRef = useRef(false);
  /** Prevents scheduling submit more than once (e.g. when effect re-runs on re-render while solved). */
  const submitScheduledRef = useRef(false);
  /** Persist imageUrl so we don't switch back to loading when room-state overwrites payload (same as BonusLevelHost). */
  const imageUrlRef = useRef<string>(imageUrl);
  if (imageUrl) imageUrlRef.current = imageUrl;
  const displayImageUrl = imageUrl || imageUrlRef.current;

  const isSolved = useMemo(
    () => slots[0] === 0 && slots[1] === 1 && slots[2] === 2 && slots[3] === 3,
    [slots]
  );

  /** Submit with points. If pointsOverride is provided, use it (computed at solve time); else compute now. */
  const submitSolved = useCallback(
    (pointsOverride?: number) => {
      if (submittedRef.current || disabled) return;
      submittedRef.current = true;
      setSubmitted(true);
      const points =
        typeof pointsOverride === 'number'
          ? pointsOverride
          : Math.max(0, Math.round(100 * (Math.max(0, roundEndTimeMs - Date.now()) / timeLimitMs)));
      onSubmit('done', points);
    },
    [disabled, roundEndTimeMs, timeLimitMs, onSubmit]
  );

  // When all 4 pieces are set: blink green (correct) or red (wrong) for 1s.
  // On success submit immediately (same as WordOrder) so effect cleanup / parent re-renders can't cancel the send.
  // Server computes puzzle points from server time; we send client points for ack display only.
  useEffect(() => {
    const allFilled = slots.every((s) => s !== null);
    if (!allFilled) {
      allFilledFlashRef.current = false;
      submitScheduledRef.current = false;
      return;
    }
    // Only skip when we already showed the *wrong* flash and we're still not solved (avoid re-flashing wrong).
    if (allFilledFlashRef.current && !isSolved) return;
    allFilledFlashRef.current = true;
    setBorderFlash(isSolved ? 'success' : 'wrong');
    const t1 = setTimeout(() => {
      setBorderFlash(null);
    }, 1000);
    if (isSolved && !submittedRef.current && !disabled && !submitScheduledRef.current) {
      submitScheduledRef.current = true;
      const timeRemainingAtSolve = Math.max(0, roundEndTimeMs - Date.now());
      const pointsAtSolve = Math.max(0, Math.round(100 * (timeRemainingAtSolve / timeLimitMs)));
      submitSolved(pointsAtSolve);
    }
    return () => clearTimeout(t1);
  }, [slots, isSolved, disabled, submitSolved, roundEndTimeMs, timeLimitMs]);

  const isInactive = disabled || submitted;

  const themeBorder = '2px solid var(--color-primary)';
  const emptySlotOutline = '2px dotted rgba(var(--color-primary-rgb), 0.72)';
  const emptySlotBackground = 'linear-gradient(135deg, rgba(var(--color-secondary-rgb), 0.08) 0%, rgba(var(--color-primary-rgb), 0.06) 100%)';
  const selectedOutline = '2px solid #34D399';
  const selectedBorder = '2px solid #34D399'; // for pool pieces (border so piece has visible edge)
  const selectedShadow = '0 0 8px rgba(52, 211, 153, 0.5)';

  // Tap-to-select (same pattern as WordOrderPlayer): select then place, or swap, or deselect
  const handleTapSlot = (slotIndex: number) => {
    if (isInactive) return;
    if (selected?.source === 'pool') {
      const quadrant = pool[selected.index];
      const existing = slots[slotIndex];
      setPool((prev) => {
        const removed = prev.filter((_, i) => i !== selected.index);
        if (existing !== null) removed.push(existing);
        return removed;
      });
      setSlots((prev) => {
        const next = [...prev];
        next[slotIndex] = quadrant;
        return next;
      });
      setSelected(null);
      return;
    }
    if (selected?.source === 'slot') {
      if (selected.index === slotIndex) {
        setSelected(null);
        return;
      }
      setSlots((prev) => {
        const next = [...prev];
        const a = prev[selected.index];
        const b = prev[slotIndex];
        next[selected.index] = b;
        next[slotIndex] = a;
        return next;
      });
      setSelected(null);
      return;
    }
    if (slots[slotIndex] !== null) setSelected({ source: 'slot', index: slotIndex });
  };

  const handleTapPool = (poolIndex: number) => {
    if (isInactive) return;
    if (selected?.source === 'slot') {
      const quadrant = pool[poolIndex];
      const slotVal = slots[selected.index];
      setSlots((prev) => {
        const next = [...prev];
        next[selected.index] = quadrant;
        return next;
      });
      setPool((prev) => {
        const removed = prev.filter((_, i) => i !== poolIndex);
        if (slotVal !== null) removed.push(slotVal);
        return removed;
      });
      setSelected(null);
      return;
    }
    if (selected?.source === 'pool' && selected.index === poolIndex) {
      setSelected(null);
    } else {
      setSelected({ source: 'pool', index: poolIndex });
    }
  };

  const handleDragStart = (source: 'slot' | 'pool', index: number) => {
    if (isInactive) return;
    if (source === 'slot' && slots[index] === null) return;
    setDragging({ source, index });
    setSelected(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnSlot = (slotIndex: number) => {
    if (!dragging || isInactive) return;
    if (dragging.source === 'pool') {
      const quadrant = pool[dragging.index];
      const existing = slots[slotIndex];
      setPool((prev) => {
        const removed = prev.filter((_, i) => i !== dragging!.index);
        if (existing !== null) removed.push(existing);
        return removed;
      });
      setSlots((prev) => {
        const next = [...prev];
        next[slotIndex] = quadrant;
        return next;
      });
    } else {
      const slotVal = slots[dragging.index];
      const existing = slots[slotIndex];
      if (slotVal === null) {
        setDragging(null);
        return;
      }
      setSlots((prev) => {
        const next = [...prev];
        next[dragging!.index] = existing;
        next[slotIndex] = slotVal;
        return next;
      });
    }
    setDragging(null);
  };

  const handleDropOnPool = () => {
    if (!dragging || isInactive) return;
    if (dragging.source === 'slot') {
      const quadrant = slots[dragging.index];
      if (quadrant === null) {
        setDragging(null);
        return;
      }
      setSlots((prev) => {
        const next = [...prev];
        next[dragging!.index] = null;
        return next;
      });
      setPool((prev) => [...prev, quadrant]);
    }
    setDragging(null);
  };

  const handleDragEnd = () => setDragging(null);

  const wrapperStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '55vh',
    padding: '1.5rem 1rem',
    margin: '0 auto',
    width: '100%',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    boxSizing: 'border-box',
  };
  // Mobile-first: box and pieces 75% viewport width, square; cap for large screens
  const boxWidth = 'min(75vw, 420px)';
  const pieceMaxWidth = 'min(22vw, 100px)';
  const innerBlockStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: boxWidth,
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };
  const boxWrapperStyle: React.CSSProperties = {
    width: '75vw',
    maxWidth: 420,
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  // Keyframes for success/wrong border blink (1s)
  const borderBlinkStyle = `
    @keyframes puzzleBorderSuccess {
      0%, 100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.3); }
      50% { box-shadow: 0 0 16px 4px rgba(52, 211, 153, 0.7); }
    }
    @keyframes puzzleBorderWrong {
      0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.3); }
      50% { box-shadow: 0 0 16px 4px rgba(239, 68, 68, 0.7); }
    }
  `;

  const gridBoxBaseStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    aspectRatio: '1',
    width: '100%',
    overflow: 'hidden',
    gap: 0,
    padding: 0,
    background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.08) 0%, rgba(var(--color-secondary-rgb), 0.12) 100%)',
  };
  const gridBoxClassName = 'player-surface-panel';

  // Intro or no image yet: show empty bordered box and "Loading puzzle pieces..." below
  if (isIntro || !displayImageUrl) {
    return (
      <div style={{ ...wrapperStyle, touchAction: 'auto' }}>
        <style>{borderBlinkStyle}</style>
        <div style={innerBlockStyle}>
          <div style={boxWrapperStyle}>
            <div
              className={gridBoxClassName}
              style={{
                ...gridBoxBaseStyle,
                border: themeBorder,
                borderRadius: 12,
              }}
            >
              {[0, 1, 2, 3].map((slotIndex) => (
                <div
                  key={slotIndex}
                  style={{
                    minHeight: 0,
                    outline: emptySlotOutline,
                    outlineOffset: -1,
                    background: emptySlotBackground,
                  }}
                />
              ))}
            </div>
          </div>
          <p className="bonus-player-hint" style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            {introCountdownSeconds != null && introCountdownSeconds > 0
              ? t('bonus.player.piecesUnlockIn', { seconds: introCountdownSeconds })
              : t('bonus.player.loadingPuzzlePieces')}
          </p>
        </div>
      </div>
    );
  }

  const gridBorder =
    borderFlash === 'success'
      ? '2px solid #22c55e'
      : borderFlash === 'wrong'
        ? '2px solid #ef4444'
        : themeBorder;
  const gridAnimation =
    borderFlash === 'success'
      ? 'puzzleBorderSuccess 1s ease'
      : borderFlash === 'wrong'
        ? 'puzzleBorderWrong 1s ease'
        : undefined;

  return (
    <div style={{ ...wrapperStyle, touchAction: 'none' }}>
      <style>{borderBlinkStyle}</style>
      <div style={innerBlockStyle}>
        <div>
          {/* Empty 2×2 box with theme border (slots) — wrapped so box is centered */}
          <div style={boxWrapperStyle}>
            <div
              className={gridBoxClassName}
              style={{
                ...gridBoxBaseStyle,
                border: gridBorder,
                borderRadius: 12,
                animation: gridAnimation,
              }}
            >
        {[0, 1, 2, 3].map((slotIndex) => {
          const quadrant = slots[slotIndex];
          const pos = quadrant !== null ? QUADRANT_POSITIONS[quadrant] : null;
          const isSelectedSlot = selected?.source === 'slot' && selected.index === slotIndex;
          const isDraggingSlot = dragging?.source === 'slot' && dragging.index === slotIndex;
          return (
            <div
              key={slotIndex}
              onClick={() => handleTapSlot(slotIndex)}
              onDragOver={handleDragOver}
              onDrop={() => handleDropOnSlot(slotIndex)}
              draggable={quadrant !== null && !isInactive}
              onDragStart={() => quadrant !== null && handleDragStart('slot', slotIndex)}
              onDragEnd={handleDragEnd}
              style={{
                minHeight: 0,
                backgroundImage: pos ? `url(${displayImageUrl})` : 'none',
                backgroundColor: pos ? 'transparent' : emptySlotBackground,
                backgroundSize: '200% 200%',
                backgroundPosition: pos ? `${pos.x} ${pos.y}` : 'unset',
                backgroundRepeat: 'no-repeat',
                border: 'none',
                outline: isSelectedSlot ? selectedOutline : quadrant !== null ? 'none' : emptySlotOutline,
                outlineOffset: -1,
                cursor: isInactive ? 'default' : 'pointer',
                boxShadow: isSelectedSlot ? selectedShadow : 'none',
                opacity: isDraggingSlot ? 0.7 : 1,
                transition: 'outline 0.15s, box-shadow 0.15s',
              }}
            />
          );
        })}
            </div>
          </div>

          {/* 4 pieces in pool (below box) — tap to select, then tap slot to place; or drag. Same pattern as WordOrder. */}
          <div
        onDragOver={handleDragOver}
        onDrop={handleDropOnPool}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 10,
          marginTop: '1.25rem',
          width: '75vw',
          maxWidth: 420,
          minHeight: 80,
        }}
      >
        {pool.map((quadrant, poolIndex) => {
          const pos = QUADRANT_POSITIONS[quadrant];
          const isSelectedPool = selected?.source === 'pool' && selected.index === poolIndex;
          const isDraggingPool = dragging?.source === 'pool' && dragging.index === poolIndex;
          return (
            <div
              key={`pool-${poolIndex}-${quadrant}`}
              onClick={(e) => {
                e.stopPropagation();
                handleTapPool(poolIndex);
              }}
              draggable={!isInactive}
              onDragStart={() => handleDragStart('pool', poolIndex)}
              onDragEnd={handleDragEnd}
              style={{
                aspectRatio: '1',
                maxWidth: pieceMaxWidth,
                width: '100%',
                margin: '0 auto',
                backgroundImage: `url(${displayImageUrl})`,
                backgroundSize: '200% 200%',
                backgroundPosition: `${pos.x} ${pos.y}`,
                border: isSelectedPool ? selectedBorder : themeBorder,
                borderRadius: 8,
                cursor: isInactive ? 'default' : 'pointer',
                boxShadow: isSelectedPool ? selectedShadow : 'none',
                opacity: isDraggingPool ? 0.7 : 1,
                transition: 'border 0.15s, box-shadow 0.15s',
              }}
            />
          );
        })}
          </div>

          {!submitted && (
            <div className="bonus-player-hint-wrap">
              <p className="bonus-player-hint" style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {footerHint ?? 'Drag or tap pieces into the box to match the host screen'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
